import authService from '@platform/core/src/services/authService';
import config from '@platform/core/src/config';

const API_BASE_URL = config.API_BASE_URL;
const LOCATIONS_API_URL = `${API_BASE_URL}/api/v1/locations`;

/**
 * Fetches all workout locations for the current user.
 * @returns {Promise<Array<Object>>} An array of location objects.
 * @throws {Error} If fetching fails.
 */
export const getMyLocations = async () => {
  const url = LOCATIONS_API_URL;
  try {
    const response = await authService.authFetch(url);
    if (!response.ok) {
      let errorDetail = `Failed to fetch user locations: ${response.status}`;
      try {
        const errorData = await response.json();
        errorDetail = errorData.detail || errorDetail;
      } catch (e) { /* ignore */ }
      throw new Error(errorDetail);
    }
    const locations = await response.json();
    return locations;
  } catch (error) {
    console.error('[workoutsmart/locationApi] Error fetching user locations:', error);
    authService.trackNetworkError('getMyLocations', error);
    throw error;
  }
};

/**
 * Creates a new workout location for the authenticated user.
 * @param {Object} locationData - Data for the new location (LocationCreateRequest).
 * @param {string} locationData.name - The name of the location.
 * @param {string|null} [locationData.description] - Optional description.
 * @param {Array<string>} [locationData.feature_ids] - Array of feature IDs.
 * @returns {Promise<Object>} The created location object.
 * @throws {Error} If creation fails.
 */
export const createLocation = async (locationData) => {
  const url = LOCATIONS_API_URL;
  try {
    const response = await authService.authFetch(url, {
      method: 'POST',
      body: locationData,
    });
    if (!response.ok) {
      let errorDetail = `Failed to create location: ${response.status}`;
      try {
        const errorData = await response.json();
        errorDetail = errorData.detail || errorDetail;
      } catch (e) { /* ignore */ }
      throw new Error(errorDetail);
    }
    const newLocation = await response.json();
    return newLocation;
  } catch (error) {
    console.error('[workoutsmart/locationApi] Error creating location:', error);
    authService.trackNetworkError('createLocation', error);
    throw error;
  }
};

/**
 * Updates an existing workout location.
 * @param {string} locationId - The ID of the location to update.
 * @param {Object} locationData - Data to update (LocationUpdateRequest).
 * @returns {Promise<Object>} The updated location object.
 * @throws {Error} If updating fails.
 */
export const updateLocation = async (locationId, locationData) => {
  const url = `${LOCATIONS_API_URL}/${locationId}`;
  try {
    const response = await authService.authFetch(url, {
      method: 'PUT',
      body: locationData,
    });
    if (!response.ok) {
      let errorDetail = `Failed to update location: ${response.status}`;
      try {
        const errorData = await response.json();
        errorDetail = errorData.detail || errorDetail;
      } catch (e) { /* ignore */ }
      throw new Error(errorDetail);
    }
    const updatedLocation = await response.json();
    return updatedLocation;
  } catch (error) {
    console.error('[workoutsmart/locationApi] Error updating location:', error);
    authService.trackNetworkError('updateLocation', error);
    throw error;
  }
};

/**
 * Deletes a workout location.
 * @param {string} locationId - The ID of the location to delete.
 * @returns {Promise<void>}
 * @throws {Error} If deletion fails.
 */
export const deleteLocation = async (locationId) => {
  const url = `${LOCATIONS_API_URL}/${locationId}`;
  try {
    const response = await authService.authFetch(url, {
      method: 'DELETE',
    });
    if (!response.ok) {
      let errorDetail = `Failed to delete location: ${response.status}`;
      try {
        const errorData = await response.json();
        errorDetail = errorData.detail || errorDetail;
      } catch (e) { /* ignore */ }
      throw new Error(errorDetail);
    }
  } catch (error) {
    console.error('[workoutsmart/locationApi] Error deleting location:', error);
    authService.trackNetworkError('deleteLocation', error);
    throw error;
  }
};